<script>
  import { createEventDispatcher } from "svelte";
  import CloseButton from "../ui/CloseButton.svelte";
  import LangSpan from "../ui/LangSpan.svelte";
  import ColorBar from "./ColorBar.svelte";
  import Histogram from "./Histogram.svelte";
  import StreetTile from "./StreetTile.svelte";
  import CATEGORY_MAP from "../categories";
  import Spacer from "../ui/Spacer.svelte";
  import { t, lang } from "../i18n";
  import stats from "../../data/curieuzenair-stats.json";

  const dispatch = createEventDispatcher();
  const onClose = () => {
    dispatch("close");
  };
  let language;
  lang.subscribe((l) => (language = l));

  export let feature;
  $: no2label = `no2-${feature.properties.no2cat}`;
  $: no2cat = CATEGORY_MAP.find((cat) => cat.category === feature.properties.no2cat);
  $: muniStats = stats.find((stat) => stat.muni == feature.properties.muni);
  $: muni = muniStats[language];
  $: floorLangKey = `sidebar-floor-${feature.properties.floor > 3 ? "n" : feature.properties.floor}`;
</script>

<aside class={feature ? "visible" : ""}>
  <CloseButton on:click={onClose} />
  <div class="content">
    <h1 class="title"><LangSpan key="sidebar-title" /></h1>
    <p class="no2-label"><LangSpan key={no2label} /></p>
    <p class="no2-value">
      {feature.properties.no2.toFixed(2).replace(/\./, ",")}<span class="no2-unit">
        <span>µg/m<sup style="font-size: 0.6em">3</sup></span></span
      >
    </p>
    <ColorBar value={feature.properties.no2cat} average={muniStats.no2cat} {muni} />

    <Spacer height={3} />
    <h2><LangSpan key="sidebar-meaning-header" replacements={{ muni }} /></h2>
    <Histogram stats={muniStats} no2cat={feature.properties.no2cat} />

    <Spacer height={3} />
    <h2><LangSpan key="sidebar-street-header" /></h2>
    <div class="street-grid">
      <StreetTile
        label={t("sidebar-street-traffic", $lang)}
        icon="traffic"
        text={t(feature.properties.traffic, $lang)}
      />
      <StreetTile
        label={t("sidebar-street-housing", $lang)}
        icon="housing"
        text={t(feature.properties.housing, $lang)}
      />
      <StreetTile label={t("sidebar-street-green", $lang)} icon="green" text={t(feature.properties.green, $lang)} />
      <StreetTile label={t("sidebar-street-road", $lang)} icon="road" text={t(feature.properties.road, $lang)} />
    </div>
    <p class="floor">
      <LangSpan key={floorLangKey} replacements={{ floor: feature.properties.floor }} />
    </p>

    <p class="disclaimer">
      <LangSpan key="sidebar-disclaimer" />
    </p>

    <p class="cnid">Curieuzeneuzen ID: {feature.properties.cnId}</p>
  </div>
</aside>

<style>
  aside {
    position: fixed;
    top: 100px;
    right: 0;
    width: 100%;
    background: rgba(255, 255, 255, 0.95);
    height: 100%;
    overflow: hidden;
    overflow-y: auto;
    transform: translate(420px, 0);
    transition: transform 0.3s ease-in-out;
    box-shadow: -10px 0 20px rgba(0, 0, 0, 0.2);
    color: #333;
    z-index: 10;
    animation: slide-up 0.3s ease-in-out;
  }
  aside.visible {
    transform: translate(0, 0);
  }
  .content {
    padding: 36px 24px 150px 24px;
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    align-items: center;
  }
  .title {
    font-size: 1.2rem;
    line-height: 1.2;
    text-align: center;
  }
  .no2-label {
    font-family: sans-serif;
    font-size: 18px;
    text-transform: uppercase;
    text-align: center;
    margin: 0 0 5px 0;
  }
  .no2-value {
    font-size: 55px;
    text-align: center;
    margin: 0 0 20px 0;
  }
  .no2-unit {
    font-size: 14px;
    padding: 0 0 0 5px;
    color: #666;
  }
  h2 {
    font-size: 1.3rem;
    line-height: 1;
    margin: 0 0 24px 0;
    text-align: center;
  }

  .street-grid {
    display: grid;
    grid-template-columns: repeat(2, 120px);
    gap: 20px 40px;
  }

  .floor {
    padding-top: 8px;
    font-size: 13px;
    font-weight: bold;
    text-align: center;
    padding: 5px 0;
  }

  .disclaimer {
    font-size: 14px;
    color: #666;
    text-align: center;
  }

  .cnid {
    font-size: 0.75rem;
    color: #999;
    border-top: 1px solid rgba(0, 0, 0, 0.2);
    padding-top: 0.7rem;
    margin-top: 1rem;
  }

  @media (min-width: 768px) {
    aside {
      width: 420px;
      animation-name: slide-in;
    }
  }

  @keyframes slide-in {
    from {
      transform: translate(420px, 0);
    }
    to {
      transform: translate(0, 0);
    }
  }

  @keyframes slide-up {
    from {
      transform: translate(0, 100vh);
    }
    to {
      transform: translate(0, 0);
    }
  }
</style>
